import React, { useState, useEffect } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Table, 
  Spinner, 
  Alert, 
  Badge, 
  Form, 
  InputGroup, 
  Button 
} from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { loadingCityApi } from "../../api/apiSystem";
import PaginationComponent from "../PaginationComponent";
import { useSearchFilter } from "../useSearchFilter";

export default function LoadingCities() {
  const { t } = useTranslation();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination State
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  useEffect(() => {
    fetchLoadingCities();
  }, []);

  const fetchLoadingCities = async (page = 1) => {
    try {
      setLoading(true);
      setError("");
      
      const response = await loadingCityApi.index({
        page: page,
        per_page: pagination.per_page,
      });
      
      if (response.success) {
        console.log("Loading cities data:", response.data);
        
        const citiesData = response.data.data || response.data;
        setCities(citiesData);

        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.length
        });
      } else {
        setError(response.message || t('errors.fetchCities'));
        setCities([]);
      }
    } catch (err) {
      console.error("Error fetching loading cities:", err);
      setError(t('errors.fetchError'));
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCities = useSearchFilter(cities, searchTerm, [
    "group_name",
    "postal_codes"
  ]);

  // 🔹 Handle Page Change
  const handlePageChange = (page) => {
    fetchLoadingCities(page);
  };

  // 🔹 Clear Search
  const clearSearch = () => {
    setSearchTerm("");
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1
    }));
    setTimeout(() => fetchLoadingCities(1), 0);
  };

  const getPostalCodesCount = (postalCodes) => {
    if (!postalCodes || !Array.isArray(postalCodes)) return 0;
    
    if (searchTerm) {
      const filteredCodes = postalCodes.filter(code => 
        code.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      return filteredCodes.length;
    }
    
    return postalCodes.length;
  };

  const renderPostalCodes = (postalCodes) => {
    if (!postalCodes || !Array.isArray(postalCodes)) return "N/A";
    
    let displayCodes = postalCodes;
    if (searchTerm) {
      displayCodes = postalCodes.filter(code => 
        code.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return (
      <div 
        style={{ 
          maxHeight: '150px', 
          overflowY: 'auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '5px',
          justifyContent: 'center'
        }}
      >
        {displayCodes.map((code, index) => {
          const codeString = code.toString();
          const shouldHighlight = searchTerm && 
            codeString.toLowerCase().includes(searchTerm.toLowerCase());
          
          return (
            <Badge 
              key={index} 
              bg={shouldHighlight ? "warning" : "outline-secondary"}
              text={shouldHighlight ? "dark" : "dark"}
              style={{ 
                border: '1px solid #dee2e6',
                padding: '4px 8px',
                fontSize: '0.75rem'
              }}
            >
              {searchTerm && shouldHighlight ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: codeString.replace(
                      new RegExp(searchTerm, "gi"),
                      (match) => `<mark class="bg-warning">${match}</mark>`
                    ),
                  }}
                />
              ) : (
                codeString
              )}
            </Badge>
          );
        })}
      </div>
    );
  };

  if (loading && cities.length === 0) {
    return (
      <>
        <NavbarMenu />
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status" style={{ color: "#ff7b00" }}>
              <span className="visually-hidden">{t('common.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('loadingCities.loading')}</p>
          </div>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <NavbarMenu />
      <Container fluid className="py-4">
        <h4 className="text-center fw-bold mb-4" style={{ color: "#ff7b00" }}>
          {t('loadingCities.title')}
        </h4>

        {/* Search Bar */}
        <Row className="mb-3 mt-5 align-items-center">
          <Col xs={12} md={6} className="d-flex align-items-center mb-2 mb-md-0">
            <span className="me-2 fw-bold">{t('clientTable.show')}</span>
            <Form.Select 
              size="sm" 
              style={{ width: "80px" }}
              value={pagination.per_page}
              onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Form.Select>
            <span className="ms-2">{t('clientTable.entries')}</span>
          </Col>

          {/* Search Filter Search Bar */}
          <Col xs={12} md={6} className="d-flex justify-content-md-end">
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('loadingCities.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={clearSearch}
                  title={t('clientTable.clearSearch')}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>

        {/* Results Count */}
        {!loading && (
          <Row className="mb-3">
            <Col>
              <div className="text-muted">
                {t('loadingCities.showing')} {filteredCities.length} {t('loadingCities.of')} {pagination.total} {t('loadingCities.cities')}
                {searchTerm && ` ${t('loadingCities.for')} "${searchTerm}"`}
              </div>
            </Col>
          </Row>
        )}

        {error && (
          <Alert variant="danger" className="d-flex align-items-center">
            {error}
          </Alert>
        )}

        <div className="table-responsive">
          {filteredCities.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">
                {searchTerm ? t('loadingCities.noResults') : t('loadingCities.noCitiesFound')}
              </p>
              {searchTerm && (
                <Button variant="outline-primary" onClick={clearSearch}>
                  {t('loadingCities.clearSearch')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table bordered responsive className="align-middle">
                <thead className="fw-bold bg-dark text-white">
                  <tr>
                    <th className="text-center">{t('loadingCities.id')}</th>
                    <th className="text-center">{t('loadingCities.groupName')}</th>
                    <th className="text-center">{t('loadingCities.postalCodes')}</th>
                    <th className="text-center">{t('loadingCities.postalCodesCount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCities.map((city, idx) => {
                    const groupName = city.group_name || "";
                    const postalCodesCount = getPostalCodesCount(city.postal_codes);
                    const totalPostalCodes = city.postal_codes?.length || 0;
                    const isFiltered = searchTerm && postalCodesCount !== totalPostalCodes;
                    
                    return (
                      <tr key={city.id || idx} className={idx % 2 === 0 ? "table-light" : "table-secondary"}>
                        <td className="text-center fw-bold">
                          {city.id}
                        </td>
                        <td className="text-center">
                          {searchTerm ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: groupName.replace(
                                  new RegExp(searchTerm, "gi"),
                                  (match) => `<mark class="bg-warning">${match}</mark>`
                                ),
                              }}
                            />
                          ) : (
                            <strong>{groupName}</strong>
                          )}
                        </td>
                        <td>
                          {renderPostalCodes(city.postal_codes)}
                        </td>
                        <td className="text-center">
                          <div className="d-flex flex-column align-items-center">
                            <Badge 
                              bg="info" 
                              style={{ 
                                fontSize: '1rem', 
                                padding: '8px 12px',
                                minWidth: '80px'
                              }}
                            >
                              {postalCodesCount} {t('loadingCities.codes')}
                            </Badge>
                            {isFiltered && (
                              <>
                                <Badge 
                                  bg="warning" 
                                  text="dark" 
                                  className="mt-1 fs-6"
                                >
                                  {t('loadingCities.filtered')}
                                </Badge>
                                <small className="text-muted mt-1">
                                  ({t('loadingCities.of')} {totalPostalCodes})
                                </small>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* Pagination */}
              {!loading && pagination.total > 0 && (
                <PaginationComponent
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={fetchLoadingCities}
                  disabled={loading}
                />
              )}
            </>
          )}
        </div>
      </Container>
      <Footer />
    </>
  );
}